import React, { useMemo, useState } from "react";
import { DayPicker, useDayPicker } from "react-day-picker";
import { es } from "date-fns/locale";
import { format } from "date-fns";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
} from "@material-tailwind/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  PlusIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { parseYmdAsLocalNoon, dateToYmd } from "../../utils/bookingWindowMx";

import "react-day-picker/style.css";

/* ── Custom DayPicker caption: title absolutely centered, arrows on edges ── */
function CustomCaption({ calendarMonth }) {
  const { previousMonth, nextMonth, goToMonth } = useDayPicker();
  return (
    <div className="relative flex items-center justify-center py-1 mb-2" style={{ width: "calc(7 * 2.5rem)" }}>
      <button
        type="button"
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className="absolute left-0 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand-lavender/25 bg-white text-brand-navy hover:bg-brand-lilac/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Mes anterior"
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden />
      </button>

      <span className="text-[0.9375rem] font-semibold text-brand-navy capitalize">
        {format(calendarMonth.date, "MMMM yyyy", { locale: es })}
      </span>

      <button
        type="button"
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className="absolute right-0 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand-lavender/25 bg-white text-brand-navy hover:bg-brand-lilac/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        aria-label="Mes siguiente"
      >
        <ChevronRightIcon className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

/* ── Custom day button: handles selected highlight reliably ── */
function CustomDayButton({ day, modifiers, ...btnProps }) {
  const sel      = modifiers.selected;
  const today    = modifiers.today;
  const outside  = modifiers.outside;
  const disabled = modifiers.disabled;

  const base = "h-9 w-9 rounded-full text-[0.875rem] font-medium transition-colors focus:outline-none";

  let extra = "";
  if (disabled) extra = " opacity-35 cursor-not-allowed";
  else if (outside) extra = " opacity-40";

  if (!sel && today && !disabled) extra += " font-bold";

  /* inline style guarantees color regardless of Tailwind purging */
  const style = sel
    ? { backgroundColor: "#3d3a8c", color: "#ffffff", fontWeight: 600 }
    : today && !disabled
    ? { color: "#7c6fd4" }
    : {};

  return <button {...btnProps} className={base + extra} style={style} />;
}

function formatDate(ymd) {
  try {
    return new Date(ymd + "T12:00:00").toLocaleDateString("es-MX", {
      weekday: "short",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return ymd;
  }
}

/**
 * Lista de días bloqueados + modal de selección.
 * @param {"admin"|"self"} [variant="admin"]
 */
export default function PsychBlockedDatesPicker({
  blockedYmd = [],
  onChange,
  variant = "admin",
}) {
  const isSelf = variant === "self";
  const [open, setOpen] = useState(false);
  // draft selection inside modal (starts as copy of current)
  const [draft, setDraft] = useState([]);

  function openModal() {
    setDraft([...(blockedYmd || [])]);
    setOpen(true);
  }

  function closeModal() {
    setOpen(false);
  }

  function applyModal() {
    onChange([...draft].sort());
    setOpen(false);
  }

  function removeBlocked(ymd) {
    onChange((blockedYmd || []).filter((d) => d !== ymd));
  }

  /* ── Draft selection inside modal ── */
  const draftDates = useMemo(
    () => draft.filter(Boolean).map((s) => parseYmdAsLocalNoon(s)),
    [draft]
  );

  function handleDraftSelect(dates) {
    if (!dates?.length) { setDraft([]); return; }
    setDraft([...dates].map((d) => dateToYmd(d)).filter(Boolean).sort());
  }

  const sorted = [...(blockedYmd || [])].filter(Boolean).sort();
  const draftCount = draft.length;

  return (
    <>
      {/* ── Main section: list + add button ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="portal-meta">
            {isSelf
              ? "Días en los que no podrás atender citas."
              : "Días en los que no habrá consultas disponibles."}
          </p>
          <button
            type="button"
            onClick={openModal}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-lavender bg-white px-3 py-1.5 text-[0.8125rem] font-medium text-brand-navy hover:bg-brand-lilac/20 transition-colors"
          >
            <PlusIcon className="h-4 w-4" aria-hidden />
            Agregar días
          </button>
        </div>

        {sorted.length === 0 ? (
          <div className="flex items-center gap-3 rounded-xl border border-dashed border-brand-lavender/40 bg-white px-4 py-5 text-brand-gray">
            <CalendarDaysIcon className="h-5 w-5 shrink-0 opacity-50" aria-hidden />
            <span className="text-[0.875rem]">Sin días bloqueados</span>
          </div>
        ) : (
          <ul className="divide-y divide-brand-lavender/10 rounded-xl border border-brand-lavender/20 bg-white overflow-hidden">
            {sorted.map((ymd) => (
              <li
                key={ymd}
                className="flex items-center justify-between px-4 py-2.5 hover:bg-brand-lilac/10 transition-colors"
              >
                <span className="text-[0.875rem] text-brand-dark capitalize">
                  {formatDate(ymd)}
                </span>
                <button
                  type="button"
                  onClick={() => removeBlocked(ymd)}
                  className="ml-4 inline-flex h-7 w-7 items-center justify-center rounded-lg text-brand-gray hover:bg-rose-50 hover:text-rose-600 transition-colors"
                  aria-label={`Quitar ${ymd}`}
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Modal with calendar ── */}
      <Dialog
        open={open}
        handler={closeModal}
        size="sm"
        className="!max-w-sm w-full"
      >
        <DialogHeader className="flex items-center justify-between pb-1">
          <span className="portal-section-title">Seleccionar días bloqueados</span>
          <button
            type="button"
            onClick={closeModal}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-brand-gray hover:bg-brand-lilac/20 transition-colors"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-4 w-4" aria-hidden />
          </button>
        </DialogHeader>

        <DialogBody className="px-4 pt-0 pb-2">
          <p className="portal-meta mb-3">
            Toca los días para marcarlos o desmarcarlo.{" "}
            <span className="font-medium text-brand-navy">
              {draftCount > 0
                ? `${draftCount} día${draftCount === 1 ? "" : "s"} seleccionado${draftCount === 1 ? "" : "s"}.`
                : "Ninguno seleccionado."}
            </span>
          </p>

          <div className="flex justify-center">
            <DayPicker
              mode="multiple"
              locale={es}
              weekStartsOn={1}
              selected={draftDates}
              onSelect={handleDraftSelect}
              defaultMonth={new Date()}
              components={{
                Nav: () => null,
                MonthCaption: CustomCaption,
                DayButton: CustomDayButton,
              }}
              classNames={{
                root: "",
                months: "flex flex-col gap-4",
                month: "",
                month_caption: "mb-0",
                weekdays: "grid grid-cols-7 text-center mb-1",
                weekday:
                  "w-10 text-[0.6875rem] font-semibold uppercase tracking-wide text-brand-gray py-1",
                week: "grid grid-cols-7 mt-0.5",
                day: "flex justify-center p-0.5",
                day_button: "",
              }}
            />
          </div>
        </DialogBody>

        <DialogFooter className="gap-2 pt-1">
          <Button
            variant="outlined"
            size="sm"
            onClick={closeModal}
            className="border-brand-lavender/40 text-brand-navy normal-case font-medium"
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={applyModal}
            className="bg-brand-navy normal-case font-medium"
          >
            Guardar selección
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
}
